import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

type DateLike = string | Date | null | undefined

/**
 * Formats a calendar date without letting backend UTC-midnight ISO strings drift
 * to the previous local day for users west of UTC.
 */
export function formatCalendarDate(
  value: DateLike,
  format = 'MM/DD/YYYY'
): string | null {
  if (!value) return null
  return typeof value === 'string'
    ? dayjs.utc(value).format(format)
    : dayjs(value).format(format)
}

/**
 * Converts a date-like value into a stable day key. Backend strings are treated
 * as UTC calendar days; client-side Date objects retain their local day.
 */
export function toDayKey(value: DateLike): string {
  if (!value) return ''

  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return ''

  const year =
    typeof value === 'string' ? date.getUTCFullYear() : date.getFullYear()
  const monthIndex =
    typeof value === 'string' ? date.getUTCMonth() : date.getMonth()
  const day = typeof value === 'string' ? date.getUTCDate() : date.getDate()

  const month = String(monthIndex + 1).padStart(2, '0')
  const dayOfMonth = String(day).padStart(2, '0')
  return `${year}-${month}-${dayOfMonth}`
}

/**
 * Returns a stable "YYYY-MM" key for month-level comparisons.
 * - For ISO strings from the backend, uses UTC year/month to avoid timezone day-shifts.
 * - For Date objects (often created client-side), uses local year/month.
 */
export function toMonthYearKey(value: DateLike): string {
  if (!value) return ''

  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return ''

  const year =
    typeof value === 'string' ? date.getUTCFullYear() : date.getFullYear()
  const monthIndex =
    typeof value === 'string' ? date.getUTCMonth() : date.getMonth()

  const month = String(monthIndex + 1).padStart(2, '0')
  return `${year}-${month}`
}

/**
 * Returns a stable "YYYY-MM" key representing the start month of an N-month bucket,
 * anchored at January (e.g. N=2: Jan/Feb -> YYYY-01, Mar/Apr -> YYYY-03, ...).
 *
 * Useful when comparing values that should be treated as equivalent within the same
 * repeating N-month period.
 */
export function toNMonthBucketKey(value: DateLike, bucketSizeMonths: number) {
  if (bucketSizeMonths <= 0 || !Number.isFinite(bucketSizeMonths)) return ''

  if (!value) return ''
  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return ''

  const year =
    typeof value === 'string' ? date.getUTCFullYear() : date.getFullYear()
  const monthIndex =
    typeof value === 'string' ? date.getUTCMonth() : date.getMonth()

  const bucketStartMonthIndex =
    Math.floor(monthIndex / bucketSizeMonths) * bucketSizeMonths

  const month = String(bucketStartMonthIndex + 1).padStart(2, '0')
  return `${year}-${month}`
}

/**
 * Convenience helper for bi-monthly comparisons (2-month repeating buckets).
 */
export function toTwoMonthBucketKey(value: DateLike) {
  return toNMonthBucketKey(value, 2)
}

/**
 * Returns true if two values represent the same UTC calendar day.
 *
 * Use this instead of `new Date(a).toDateString() === new Date(b).toDateString()`
 * when comparing backend ISO strings: `toDateString()` formats in local time,
 * which flips the day across timezone boundaries (e.g. UTC midnight reads as
 * the previous day in PST), causing matching dates to incorrectly compare as
 * different.
 */
export function isSameUtcDay(a: DateLike, b: DateLike): boolean {
  if (!a || !b) return false
  const da = typeof a === 'string' ? new Date(a) : a
  const db = typeof b === 'string' ? new Date(b) : b
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return false
  return (
    da.getUTCFullYear() === db.getUTCFullYear() &&
    da.getUTCMonth() === db.getUTCMonth() &&
    da.getUTCDate() === db.getUTCDate()
  )
}

/**
 * A month range is valid when start's month is not after end's month
 * (day-of-month is irrelevant — these come from MM/YYYY month pickers).
 * Open-ended ranges (either bound missing) are always valid.
 */
export function isValidMonthRange(
  startDate?: Date | null,
  endDate?: Date | null
): boolean {
  if (!startDate || !endDate) return true
  return !dayjs(startDate)
    .startOf('month')
    .isAfter(dayjs(endDate).startOf('month'))
}
